import { useEffect, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router";
import { ArrowLeft, Loader2, Eye } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { useAutosave, type SaveStatus } from "@/hooks/use-autosave";
import { cn, formatRelative } from "@/lib/utils";
import { ASSET_TYPE_LABEL, ASSET_VISIBILITY_LABEL } from "@colab/shared";
import { AssetMetadataTab } from "./asset-metadata-tab";
import { AssetRowActions } from "./asset-row-actions";
import { AssetViewsTab } from "./asset-views-tab";
import type { ContentAssetRow } from "./types";
import { useAssetDetail } from "./use-asset-detail";
import { useUpdateAsset } from "./use-update-asset";

// Phase 7 sub-bucket 13. Asset detail page. Two tabs (?tab=metadata|views).
// Header carries:
//   - back link (to parent folder if exists, else /admin/content)
//   - autosaved title input (Phase 7 sub-bucket 11 useAutosave shape)
//   - chips (type, visibility, archived, view count)
//   - aggregated SaveStatus + actions menu (Edit/Move/Archive/Delete)
//
// The view-count chip links to `?tab=views`. Tab content is fully lazy via
// the conditional render: switching tabs doesn't unmount the metadata
// form state because the user is the source of truth there; the views
// tab re-mounts on each visit.

type Tab = "metadata" | "views";

function normalizeTab(raw: string | null): Tab {
  return raw === "views" ? "views" : "metadata";
}

export function AssetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = normalizeTab(searchParams.get("tab"));

  if (!id) return <Navigate to="/admin/content" replace />;

  return (
    <DetailInner
      assetId={id}
      tab={tab}
      onTabChange={(next) => {
        const sp = new URLSearchParams(searchParams);
        sp.set("tab", next);
        setSearchParams(sp, { replace: true });
      }}
    />
  );
}

function DetailInner({
  assetId,
  tab,
  onTabChange,
}: {
  assetId: string;
  tab: Tab;
  onTabChange: (next: Tab) => void;
}) {
  const query = useAssetDetail(assetId);

  if (query.isLoading) {
    return <DetailSkeleton />;
  }
  if (query.isError || !query.data) {
    return (
      <EmptyState
        icon={ArrowLeft}
        title="Couldn't load asset"
        description={
          query.error?.code === "not_found"
            ? "This asset has been deleted or never existed."
            : query.error?.message ?? "Try refreshing."
        }
        action={
          <Link to="/admin/content" className="text-sm text-accent hover:underline">
            ← Back to library
          </Link>
        }
      />
    );
  }

  return (
    <DetailBody
      assetId={assetId}
      asset={query.data.asset}
      viewCount={query.data.viewCount}
      tab={tab}
      onTabChange={onTabChange}
    />
  );
}

function DetailBody({
  assetId,
  asset,
  viewCount,
  tab,
  onTabChange,
}: {
  assetId: string;
  asset: ContentAssetRow;
  viewCount: number;
  tab: Tab;
  onTabChange: (next: Tab) => void;
}) {
  const update = useUpdateAsset();

  const [title, setTitle] = useState(asset.title);
  useEffect(() => {
    setTitle(asset.title);
  }, [asset.id, asset.title]);

  const titleSave = useAutosave<string>({
    onFlush: async (next) => {
      const trimmed = next.trim();
      if (trimmed.length === 0 || trimmed === asset.title) return;
      await update.mutateAsync({ id: assetId, patch: { title: trimmed } });
    },
    wait: 750,
  });

  // Aggregate save status across the title + metadata-tab fields. Metadata
  // tab reports via its `onSaveStatusChange` callback; we max across both.
  const [metadataStatus, setMetadataStatus] = useState<SaveStatus>("idle");
  const headerStatus = aggregateTwo(titleSave.status, metadataStatus);

  const backHref = asset.parent_asset_id
    ? `/admin/content?folder=${asset.parent_asset_id}`
    : "/admin/content";

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={backHref}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to {asset.parent_asset_id ? "folder" : "library"}
        </Link>

        <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <Input
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                titleSave.queue(e.target.value);
              }}
              onBlur={() => titleSave.flush()}
              className="!h-auto !border-transparent bg-transparent !px-0 !py-1 font-display !text-2xl !font-semibold tracking-tight !shadow-none focus-visible:!border-border focus-visible:!ring-0"
              aria-label="Asset title"
            />
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px]">
              <span className="rounded-md border bg-secondary/50 px-1.5 py-0.5 font-medium uppercase tracking-wider text-muted-foreground">
                {asset.is_folder ? "Folder" : ASSET_TYPE_LABEL[asset.asset_type]}
              </span>
              <span className="rounded-md border bg-secondary/50 px-1.5 py-0.5 font-medium text-muted-foreground">
                {ASSET_VISIBILITY_LABEL[asset.visibility]}
              </span>
              {asset.archived_at && (
                <span className="rounded-md border border-amber-500/40 bg-amber-500/5 px-1.5 py-0.5 font-medium uppercase tracking-wider text-amber-600">
                  Archived {formatRelative(asset.archived_at)}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <SaveIndicator status={headerStatus} />
            {!asset.is_folder && (
              <button
                type="button"
                onClick={() => onTabChange("views")}
                className="inline-flex items-center gap-1.5 rounded-md border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
              >
                <Eye className="h-3.5 w-3.5" />
                {viewCount} view{viewCount === 1 ? "" : "s"}
              </button>
            )}
            <AssetRowActions asset={asset} variant="outline-pill" />
          </div>
        </div>
      </div>

      <TabsNav active={tab} onChange={onTabChange} isFolder={asset.is_folder} />

      <div role="tabpanel">
        {tab === "metadata" && (
          <AssetMetadataTab
            asset={asset}
            onSaveStatusChange={setMetadataStatus}
          />
        )}
        {tab === "views" && !asset.is_folder && (
          <AssetViewsTab assetId={assetId} />
        )}
        {tab === "views" && asset.is_folder && (
          <EmptyState
            icon={Eye}
            title="Folders don't track views"
            description="View analytics applies to individual assets — open one from the folder to see its views."
          />
        )}
      </div>
    </div>
  );
}

function TabsNav({
  active,
  isFolder,
  onChange,
}: {
  active: Tab;
  isFolder: boolean;
  onChange: (next: Tab) => void;
}) {
  const tabs: ReadonlyArray<{ key: Tab; label: string }> = isFolder
    ? [{ key: "metadata", label: "Metadata" }]
    : [
        { key: "metadata", label: "Metadata" },
        { key: "views", label: "Views" },
      ];
  return (
    <nav className="flex flex-wrap items-center gap-1 border-b border-border/70">
      {tabs.map((t) => {
        const isActive = active === t.key;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={cn(
              "relative inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
            {isActive && (
              <span
                aria-hidden
                className="absolute inset-x-0 -bottom-px h-0.5 bg-accent"
              />
            )}
          </button>
        );
      })}
    </nav>
  );
}

function SaveIndicator({ status }: { status: SaveStatus }) {
  if (status === "idle") return null;
  if (status === "saving") {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Saving…
      </span>
    );
  }
  if (status === "saved") {
    return (
      <span className="text-xs text-muted-foreground">Saved</span>
    );
  }
  return (
    <span className="text-xs text-destructive">Save failed — retry</span>
  );
}

function aggregateTwo(a: SaveStatus, b: SaveStatus): SaveStatus {
  if (a === "error" || b === "error") return "error";
  if (a === "saving" || b === "saving") return "saving";
  if (a === "saved" || b === "saved") return "saved";
  return "idle";
}

function DetailSkeleton() {
  return (
    <div className="space-y-6">
      <div>
        <Skeleton className="h-3 w-24" />
        <Skeleton className="mt-2 h-9 w-1/2" />
        <Skeleton className="mt-2 h-5 w-64" />
      </div>
      <Skeleton className="h-10 w-full" />
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <Skeleton className="h-64 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    </div>
  );
}
